import React, { useCallback, useRef } from "react";
import { useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import moment from "moment";
import SelectField from "../../../components/forms/select-field";
import ItemsInputField from "../../../components/forms/items-input-field";
import { searchBinLocationAction } from "../../../redux/actions/general-action";

const headCellClass =
  "px-[8px] py-[20px] text-left text-[11px] font-semibold uppercase whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)] bg-[var(--create-table-header-bg)]";
const bodyCellClass = "px-3 py-1 align-middle whitespace-nowrap";

const computeRatio = (uom) =>
  uom?.alt_quantity && uom?.base_quantity
    ? Number(uom.base_quantity) / Number(uom.alt_quantity)
    : 1;

const DropListItemsTable = ({ disabled = false, warehouseId }) => {
  const { values, setFieldValue } = useFormikContext();
  const items = values?.items || [];
  const dispatch = useDispatch();

  const handleQtyDroppedChange = (index) => (e) => {
    const num = parseFloat(e.target.value);
    if (!(num > 0)) {
      setFieldValue(`items[${index}].dropped`, false);
    }
  };

  // The drop list is saved against its own warehouse, so the bins offered here
  // have to come from that warehouse rather than from the current session's.
  const fetchBinOptions = (inputValue, callback) => {
    if (!inputValue?.length) {
      callback([]);
      return;
    }
    dispatch(
      searchBinLocationAction(
        {
          field: "bin_location_no",
          value: inputValue,
          warehouse_id: warehouseId,
        },
        (data) =>
          callback(
            (data?.data ?? []).map((bin) => ({
              label: bin?.bin_location_no,
              value: bin?.bin_no_id,
              actualLabel: bin?.bin_location_no,
            }))
          ),
        () => callback([])
      )
    );
  };
  const fetchBinOptionsRef = useRef(fetchBinOptions);
  fetchBinOptionsRef.current = fetchBinOptions;
  const loadBinOptions = useCallback(
    debounce(
      (inputValue, callback) =>
        fetchBinOptionsRef.current(inputValue, callback),
      500
    ),
    []
  );

  return (
    <div
      style={{
        maxHeight: `calc(100% - 16.5rem)`, // Limit the container height
        height: "auto", // Let it grow naturally to fit its content
      }}
      className="mt-4 flex flex-col"
    >
      <div className="flex-1 min-h-0 overflow-auto border border-[var(--create-table-footer-border)] rounded-[8px]">
        <table className="w-full min-w-[1100px] border-collapse">
          <thead className="sticky bg-[var(--create-table-header-bg)] top-0 z-10 ">
            <tr>
              <th className={headCellClass}>Item Code</th>
              <th className={headCellClass}>Source Invoice Number</th>
              <th className={headCellClass}>Batch No</th>
              <th className={headCellClass}>Expiry</th>
              <th className={headCellClass}>From Bin Location</th>
              <th className={headCellClass}>To Bin Location</th>
              <th className={headCellClass}>Bin Priority</th>
              <th className={headCellClass}>UOM</th>
              <th className={headCellClass}>Recommended Qty</th>
              <th className={headCellClass}>Actual Qty Dropped</th>
              <th className={headCellClass}>Dropped</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td className={`${bodyCellClass} text-center`} colSpan={11}>
                  No items to drop
                </td>
              </tr>
            ) : (
              items.map((row, index) => {
                const ratio = row?.uom_ratio || 1;
                const qtyToDropDisplay = (
                  (row?.qty_to_drop_base || 0) / ratio
                ).toFixed(2);
                const uomOptions = (row?.uom_dropdown || []).map((uom) => ({
                  ...uom,
                  label: uom?.alt_uom,
                  value: uom?.item_id,
                }));
                const canCheck = parseFloat(row?.actual_qty_dropped) > 0;
                const rowDisabled = disabled || row?.already_dropped;

                return (
                  <tr
                    key={row.row_key}
                    className={`border-t border-[var(--create-table-footer-border)] ${
                      row?.already_dropped
                        ? "bg-[var(--status-badge-green-bg)]"
                        : row?.is_overflow
                        ? "bg-[var(--status-badge-orange-bg)]"
                        : ""
                    }`}
                  >
                    <td className={bodyCellClass}>{row?.item_code || "-"}</td>
                    <td className={bodyCellClass}>
                      {row?.gort_invoice_number || row?.poit_invoice_number || "-"}
                    </td>
                    <td className={bodyCellClass}>
                      {row?.item_batch_number || "-"}
                    </td>
                    <td className={bodyCellClass}>
                      {row?.item_exp_date
                        ? moment(row.item_exp_date).format("DD MMM YYYY")
                        : "-"}
                    </td>
                    <td className={bodyCellClass}>
                      {row?.from_bin_location || row?.from_bin_id || "-"}
                    </td>
                    <td className={`${bodyCellClass} min-w-[180px]`}>
                      {rowDisabled ? (
                        row?.bin_location_no || "-"
                      ) : (
                        <SelectField
                          containerClass=""
                          isContainer={false}
                          placeholder="Select Bin"
                          loadOptions={loadBinOptions}
                          defaultOptions={false}
                          name={`items[${index}].to_bin_id`}
                          isSearchable={true}
                          errMsgContainerClass="hidden"
                          valueOptions={
                            row?.to_bin_id && row?.bin_location_no
                              ? [
                                  {
                                    label: row.bin_location_no,
                                    value: row.to_bin_id,
                                  },
                                ]
                              : []
                          }
                          handleSelected={(selectedOption) => {
                            setFieldValue(
                              `items[${index}].to_bin_id`,
                              selectedOption?.value ?? ""
                            );
                            setFieldValue(
                              `items[${index}].bin_location_no`,
                              selectedOption?.actualLabel ?? ""
                            );
                            setFieldValue(`items[${index}].is_overflow`, false);
                          }}
                        />
                      )}
                      {row?.is_overflow ? " (overflow)" : ""}
                    </td>
                    <td className={bodyCellClass}>
                      {row?.bin_priority ?? "-"}
                    </td>
                    <td className={`${bodyCellClass} min-w-[160px]`}>
                      {row?.already_dropped ? (
                        row?.uom_name || "-"
                      ) : uomOptions.length > 0 ? (
                        <SelectField
                          containerClass=""
                          isContainer={false}
                          placeholder="Select UOM"
                          options={uomOptions}
                          name={`items[${index}].uom_id`}
                          isSearchable={false}
                          errMsgContainerClass="hidden"
                          isDisabled={rowDisabled}
                          handleSelected={(selectedOption) => {
                            setFieldValue(
                              `items[${index}].uom_ratio`,
                              computeRatio(selectedOption)
                            );
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className={bodyCellClass}>{qtyToDropDisplay}</td>
                    <td className={`${bodyCellClass} min-w-[140px]`}>
                      <ItemsInputField
                        type="number"
                        isLabelReq={false}
                        disabled={rowDisabled}
                        name={`items[${index}].actual_qty_dropped`}
                        placeholder="0"
                        onChange={handleQtyDroppedChange(index)}
                        inputClass="min-h-[36px] w-[110px] px-2 border border-[var(--search-input-border)] focus:outline-none focus:border-[var(--search-input-focus)] bg-[var(--search-input-bg)] text-[var(--search-input-color)] border-radius input-field-no-spin-buttons"
                      />
                    </td>
                    <td className={bodyCellClass}>
                      <input
                        type="checkbox"
                        className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
                        checked={!!row?.dropped}
                        disabled={rowDisabled || !canCheck}
                        onChange={(e) =>
                          setFieldValue(
                            `items[${index}].dropped`,
                            e.target.checked
                          )
                        }
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DropListItemsTable;
